import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getVocDetail } from "../../utils/api";

const VocCheck = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    postNumber: "",
    contact: "",
    password: "",
  });
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "contact" || name === "postNumber") {
      const numericValue = value.replace(/[^0-9]/g, "");
      setFormData(prev => ({ ...prev, [name]: numericValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const showAlert = (message: string) => {
    setModalMessage(message);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.postNumber || !formData.contact || !formData.password) {
      showAlert("필수 항목을 모두 입력해주세요.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await getVocDetail(formData.postNumber, formData.password, formData.contact);

      if (response && response.no) {
        // 성공시 상세 페이지로 이동
        navigate("/voc/view", {
          state: {
            postNumber: formData.postNumber,
            contact: formData.contact,
            password: formData.password,
          },
        });
      } else {
        showAlert(`${response}`);
      }
    } catch (error: any) {
      console.error("VOC 조회 실패:", error);
      if (error.response?.status === 500) {
        showAlert("게시글 번호를 확인해주세요.");
      } else if (error.response?.status === 404) {
        showAlert("해당 게시글을 찾을 수 없습니다.");
      } else {
        showAlert("민원 조회에 실패했습니다. 입력 정보를 확인해주세요.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main id="main-content" className="min-h-[600px]">
      {/* VOC Container */}
      <div
        className="mx-auto w-full"
        style={{
          maxWidth: "900px",
          margin: "105px auto",
        }}
      >
        {/* Title Box */}
        <div
          className="flex justify-between items-center flex-col px-10 about:px-0 sm:flex-row pb-[20px] mb-[30px]"
          style={{ borderBottom: "1px solid #e5e5e5" }}
        >
          <div className="text-center sm:text-left mb-[1rem]">
            <h1
              className="inline-block mb-[8px]"
              style={{
                fontSize: "var(--font-headline-large, 4rem)",
                fontWeight: "bold",
                color: "#202020",
              }}
            >
              문의 조회하기
            </h1>
            <p
              style={{
                fontSize: "var(--font-title-large, 1.8rem)",
                color: "#202020",
              }}
            >
              문의한 내용을 조회할 수 있습니다.
            </p>
          </div>
          <div className="voc-status-link">
            <Link
              to="/voc/submit"
              className="flex items-center justify-between transition-all hover:bg-[#ececec] hover:-translate-y-0.5 rounded-sm"
              style={{
                width: "190px",
                minHeight: "44px",
                padding: "0.5rem 1rem",
                border: "1px solid #888",
                backgroundColor: "#fff",
                fontSize: "var(--font-title-small, 1.5rem)",
                color: "#202020",
                textDecoration: "none",
              }}
            >
              문의 접수 페이지
              <span className="material-icons-round">arrow_outward</span>
            </Link>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-7 px-10 about:px-0" noValidate>
          {/* 게시글 번호 & 연락처 (모바일: flex-col 세로 정렬 / sm 이상: flex-row 가로 정렬) */}
          <div className="flex flex-col sm:flex-row gap-5">
            <div className="flex flex-col flex-1 gap-1">
              <label
                htmlFor="postNumber"
                className="after:content-['*'] after:text-red-500 after:ml-1"
                style={{
                  fontSize: "var(--font-title-medium, 1.6rem)",
                  fontWeight: 500,
                  color: "#202020",
                }}
              >
                게시글 번호
              </label>
              <input
                type="text"
                id="postNumber"
                name="postNumber"
                placeholder="게시글 번호"
                inputMode="numeric"
                value={formData.postNumber}
                onChange={handleChange}
                required
                className="rounded-sm w-full transition-colors focus:outline-none focus:border-navy focus-visible:outline-2 focus-visible:outline-navy focus-visible:outline-offset-2"
                style={{
                  minHeight: "44px",
                  padding: "0.75rem 1rem",
                  border: "1px solid #e5e5e5",
                  fontSize: "var(--font-title-small, 1.5rem)",
                  backgroundColor: "#fafafa",
                }}
              />
            </div>

            <div className="flex flex-col flex-1 gap-1">
              <label
                htmlFor="contact"
                className="after:content-['*'] after:text-red-500 after:ml-1"
                style={{
                  fontSize: "var(--font-title-medium, 1.6rem)",
                  fontWeight: 500,
                  color: "#202020",
                }}
              >
                연락처
              </label>
              <input
                type="tel"
                id="contact"
                name="contact"
                placeholder="'-'를 제외한 숫자"
                autoComplete="tel"
                inputMode="numeric"
                value={formData.contact}
                onChange={handleChange}
                required
                className="rounded-sm w-full transition-colors focus:outline-none focus:border-navy focus-visible:outline-2 focus-visible:outline-navy focus-visible:outline-offset-2"
                style={{
                  minHeight: "44px",
                  padding: "0.75rem 1rem",
                  border: "1px solid #e5e5e5",
                  fontSize: "var(--font-title-small, 1.5rem)",
                  backgroundColor: "#fafafa",
                }}
              />
            </div>
          </div>

          {/* 비밀번호 (모바일: w-full / sm 이상: 49%) */}
          <div className="flex flex-col gap-1 w-full sm:w-[49%]">
            <label
              htmlFor="password"
              className="after:content-['*'] after:text-red-500 after:ml-1"
              style={{
                fontSize: "var(--font-title-medium, 1.6rem)",
                fontWeight: 500,
                color: "#202020",
              }}
            >
              비밀번호
            </label>
            <input
              type="password"
              id="password"
              name="password"
              placeholder="6자리 미만 비밀번호"
              maxLength={6}
              autoComplete="off"
              value={formData.password}
              onChange={handleChange}
              required
              className="rounded-sm w-full transition-colors focus:outline-none focus:border-navy focus-visible:outline-2 focus-visible:outline-navy focus-visible:outline-offset-2"
              style={{
                minHeight: "44px",
                padding: "0.75rem 1rem",
                border: "1px solid #e5e5e5",
                fontSize: "var(--font-title-small, 1.5rem)",
                backgroundColor: "#fafafa",
              }}
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end mt-[10px]">
            <button
              type="submit"
              disabled={isLoading}
              className="rounded-sm cursor-pointer transition-all hover:bg-navy hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                fontSize: "var(--font-title-small, 1.6rem)",
                minHeight: "44px",
                padding: "0.5rem 2rem",
                backgroundColor: "#2A2A2F",
                color: "#fff",
              }}
            >
              {isLoading ? "조회 중..." : "조회하기"}
            </button>
          </div>
        </form>

        {/* Modal */}
        {showModal && (
          <div
            className="fixed inset-0 flex justify-center items-center z-[1000] px-[2rem] sm:px-0"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          >
            <div
              className="text-center bg-white p-[1.8rem_2rem] sm:p-[3rem_4rem]"
              style={{
                borderRadius: "var(--radius-md, 8px)",
                width: "400px",
                boxShadow: "0 5px 15px rgba(0, 0, 0, 0.3)",
              }}
            >
              <h3
                className="text-left pb-3 mb-[15px]"
                style={{
                  borderBottom: "1px solid #e5e5e5",
                  fontSize: "var(--font-title-large, 1.8rem)",
                  fontWeight: "bold",
                  color: "#202020",
                }}
              >
                입력 오류
              </h3>
              <p
                className="py-5 mb-[10px] leading-[1.5] whitespace-pre-wrap"
                style={{
                  fontSize: "var(--font-title-small, 1.6rem)",
                  color: "#202020",
                }}
              >
                {modalMessage}
              </p>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="transition-colors cursor-pointer hover:bg-black p-[10px_20px] rounded-sm"
                  style={{
                    minHeight: "44px",
                    backgroundColor: "#032C6D",
                    color: "#fff",
                    border: "none",
                    fontSize: "var(--font-title-small, 1.5rem)",
                  }}
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default VocCheck;
